import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { useApi } from '@/lib/auth'
import { usePreviewData } from '../preview-data'
import { useLocation } from './use-location'

// Mirrors sync-app-api `wan-settings.routes.ts` (kept local: that file imports server-only deps).
const wanDataRecordSchema = z.object({
  id: z.string(),
  type: z.enum(['PRIMARY', 'SECONDARY', 'TERTIARY']),
  assignmentType: z.enum(['STATIC', 'DHCP']),
  assignedCidr: z.string().nullable(),
  assignedIp: z.string().nullable(),
  subnet: z.string().nullable(),
  gw: z.string().nullable(),
  dhcp: z.boolean(),
  rangeStart: z.string().nullable(),
  rangeEnd: z.string().nullable(),
  status: z.boolean(),
  rxSpeed: z.string().nullable(),
  txSpeed: z.string().nullable(),
  carrier: z.string(),
  source: z.enum(['Vector', 'Meraki', 'Cloudflare', 'Fortinet', 'Cradlepoint']),
  extraData: z.unknown().nullable(),
})

const merakiWanInterfaceSchema = z
  .object({
    enabled: z.boolean().optional(),
    assignmentMode: z.enum(['static', 'dynamic']).optional(),
    address: z.string().optional(),
    gateway: z.string().optional(),
  })
  .passthrough()

const cloudflareWanSchema = z
  .object({
    id: z.string().optional(),
    name: z.string().optional(),
    physport: z.number().optional(),
    priority: z.number().optional(),
    vlan_tag: z.number().optional(),
    static_addressing: z
      .object({ address: z.string(), gateway_address: z.string(), secondary_address: z.string().optional() })
      .optional(),
  })
  .passthrough()

const vendorStateSchema = z.discriminatedUnion('vendor', [
  z.object({ vendor: z.literal('meraki'), interfaces: z.record(z.string(), merakiWanInterfaceSchema).nullable() }),
  z.object({ vendor: z.literal('cloudflare'), wans: z.array(cloudflareWanSchema).nullable() }),
  z.object({ vendor: z.literal('unsupported'), wans: z.null() }),
])

const wanSettingsDataSchema = z.object({
  dbRecords: z.array(wanDataRecordSchema),
  vendorState: vendorStateSchema,
  edgeVendor: z.string().nullable(),
  routerSerial: z.string().nullable(),
  lifecycle: z.enum(['CONFIGURING', 'DISPATCHING', 'PROVISIONED', 'CLOSED', 'UNMANAGED']).nullable(),
})
export type WanSettingsData = z.infer<typeof wanSettingsDataSchema>
export type WanDataRecord = z.infer<typeof wanDataRecordSchema>

const wanSettingsEnvelopeSchema = z.object({ success: z.literal(true), data: wanSettingsDataSchema })
const wanSettingsSaveEnvelopeSchema = z.object({ success: z.literal(true), data: z.object({ updated: z.boolean() }) })

/** PUT body: Meraki interface edit or Cloudflare WAN edit (mirrors the API's discriminated union). */
export type WanSettingsUpdate =
  | {
      vendor: 'meraki'
      interfaces: Record<
        string,
        {
          enabled?: boolean
          carrier?: string
          svis?: {
            ipv4:
              | {
                  assignmentMode: 'static'
                  address: string
                  gateway: string
                  nameservers?: { addresses: string[] }
                }
              | { assignmentMode: 'dynamic' }
          }
        }
      >
    }
  | {
      vendor: 'cloudflare'
      wanId: string
      carrier?: string
      staticAddressing: { address: string; gateway_address: string; secondary_address?: string }
    }

/** Pure: the API only accepts edits when lifecycle is PROVISIONED. Designs should disable edit controls otherwise. */
export const canEditWanSettings = (data: Pick<WanSettingsData, 'lifecycle'> | undefined): boolean =>
  data?.lifecycle === 'PROVISIONED'

export const wanSettingsQueryKey = (locationId: string) => ['wan-settings', locationId] as const

/** WAN settings for a location. Resolves the location UUID via `useLocation` (API path param is the id). */
export function useWanSettings(idOrCode: string) {
  const api = useApi()
  const preview = usePreviewData()
  const location = useLocation(idOrCode)
  const locationId = location.data?.id ?? null
  return useQuery({
    queryKey: preview ? ['preview', 'wan-settings'] : wanSettingsQueryKey(locationId ?? idOrCode),
    queryFn: async (): Promise<WanSettingsData> => {
      if (!locationId) throw new Error('Location not found')
      const res = await api.get(`/v1/wan-settings/${encodeURIComponent(locationId)}`, wanSettingsEnvelopeSchema)
      return res.data
    },
    enabled: !preview && locationId !== null,
    ...(preview ? { initialData: preview.wanSettings, staleTime: Number.POSITIVE_INFINITY } : {}),
  })
}

/**
 * Save WAN settings (PUT /v1/wan-settings/:locationId). Call `mutate({ locationId, body })`.
 * Invalidates the WAN settings + network overview queries on success. No-ops in /preview.
 */
export function useSaveWanSettings() {
  const api = useApi()
  const qc = useQueryClient()
  const preview = usePreviewData()
  return useMutation({
    mutationFn: async ({ locationId, body }: { locationId: string; body: WanSettingsUpdate }) => {
      if (preview) return { success: true as const, data: { updated: true } }
      return api.request(
        `/v1/wan-settings/${encodeURIComponent(locationId)}`,
        { method: 'PUT', body },
        wanSettingsSaveEnvelopeSchema,
      )
    },
    onSuccess: async (_res, { locationId }) => {
      if (preview) return
      await Promise.all([
        qc.invalidateQueries({ queryKey: wanSettingsQueryKey(locationId) }),
        qc.invalidateQueries({ queryKey: ['network-overview'] }),
      ])
    },
  })
}
